// Creators: YouTube channels the user follows for stacking/nootropics
// content. Mirrors the APK's CreatorsScreen — paste a YouTube channel URL
// (or @handle), see the latest videos. Cross-origin XML feed fetches go
// through a CORS proxy (see creator_store.ts).

import { visibleCreators, resolveChannel, addCreator, removeCreator, latestVideos, type Creator, type YoutubeVideo } from '../lib/creator_store';
import { modalPrompt, modalAlert, modalConfirm } from '../lib/modal';

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function videoCard(v: YoutubeVideo): string {
  return `
    <a href="https://www.youtube.com/watch?v=${esc(v.videoId)}" target="_blank" rel="noopener external"
       style="display:flex;gap:10px;background:var(--bg-2);border:1px solid var(--line);border-radius:8px;padding:8px;color:var(--ink);text-decoration:none;">
      <img src="${esc(v.thumbnailUrl)}" alt="" style="width:120px;height:68px;object-fit:cover;border-radius:6px;flex-shrink:0;" />
      <div style="flex:1;min-width:0;">
        <div style="font-size:13px;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${esc(v.title)}</div>
        <div style="color:var(--dim);font-size:11px;margin-top:4px;">${esc(v.channelName || '')}</div>
      </div>
    </a>`;
}

function creatorSection(creator: Creator, videos: YoutubeVideo[]): string {
  const list = videos.slice(0, 4).map(videoCard).join('') || `<p style="color:var(--dim);font-size:12px;margin:0;">No recent videos.</p>`;
  return `
    <section style="margin-bottom:18px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
        <h2 style="margin:0;font-size:15px;">
          <a href="https://www.youtube.com/channel/${esc(creator.channelId)}" target="_blank" rel="noopener external" style="color:var(--ink);text-decoration:none;">${esc(creator.name)}</a>
        </h2>
        <button class="btn creator-remove" data-cid="${esc(creator.channelId)}" style="background:transparent;border:1px solid var(--line);color:var(--dim);font-size:11px;padding:4px 8px;">Remove</button>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:8px;">${list}</div>
    </section>`;
}

export async function renderCreators(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  const repaint = async () => {
    const creators = visibleCreators();
    app.innerHTML = `
      <div style="display:flex;align-items:center;gap:12px;margin:6px 0 14px;">
        <h1 style="margin:0;font-size:24px;flex:1;">Creators</h1>
        <button id="addCreatorBtn" class="btn btn-primary">+ Add channel</button>
      </div>
      <p style="color:var(--dim);margin:0 0 16px;font-size:13px;">Latest videos from the YouTube creators you follow. Pulled from public RSS — no API key, no tracking.</p>
      <div id="creatorList">
        ${creators.length ? '<p style="color:var(--dim);font-size:13px;">Loading latest videos…</p>' : '<p style="color:var(--dim);">No creators yet. Tap <strong>+ Add channel</strong> to paste a YouTube URL or @handle.</p>'}
      </div>
    `;

    document.getElementById('addCreatorBtn')?.addEventListener('click', async () => {
      const input = await modalPrompt({
        title: 'Add YouTube creator',
        body: 'Paste a channel URL, @handle, or UCxxx... ID.',
        placeholder: 'https://www.youtube.com/@username',
      });
      if (!input) return;
      const c = await resolveChannel(input);
      if (!c) { await modalAlert({ title: 'Not found', body: 'Could not resolve that to a YouTube channel.' }); return; }
      addCreator(c);
      repaint();
    });

    if (!creators.length) return;

    // Fetch all creator feeds in parallel, then render.
    const lists = await Promise.all(creators.map((c) => latestVideos(c.channelId, c.name).then((v) => ({ c, v }))));
    const listEl = document.getElementById('creatorList');
    if (!listEl) return;
    listEl.innerHTML = lists.map(({ c, v }) => creatorSection(c, v)).join('');

    listEl.querySelectorAll<HTMLButtonElement>('.creator-remove').forEach((b) => {
      b.addEventListener('click', async () => {
        const cid = b.dataset.cid!;
        const ok = await modalConfirm({ title: 'Remove creator?', body: 'They won\'t appear here again unless you re-add them.', confirmLabel: 'Remove', danger: true });
        if (!ok) return;
        removeCreator(cid);
        repaint();
      });
    });
  };

  await repaint();
}
